'use client'

import { authFetch } from '@/lib/auth/auth-fetch'
import { useWallet } from '@/providers/WalletContext'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Hex } from 'viem'

type InitResponse = {
	walletId: string
	agentAddress: `0x${string}`
	primaryAddress: `0x${string}`
	alreadyApproved: boolean
	typedData: {
		domain: { name: string; version: string; chainId: number; verifyingContract: `0x${string}` }
		types: Record<string, Array<{ name: string; type: string }>>
		primaryType: string
		message: Record<string, unknown>
	}
	nonce: number
}

async function fetchInit(): Promise<InitResponse> {
	const r = await authFetch('/api/hyperliquid/agent/init', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
	})
	if (!r.ok) throw new Error(`agent init ${r.status}`)
	return (await r.json()) as InitResponse
}

export function useHyperliquidAgent() {
	const qc = useQueryClient()
	const { authenticated, evmAccount } = useWallet()

	const status = useQuery({
		queryKey: ['hl-agent'],
		enabled: authenticated,
		queryFn: fetchInit,
		// Server returns alreadyApproved=true once persisted; no need to poll
		staleTime: 60_000,
	})

	const approve = useMutation({
		mutationFn: async () => {
			if (!evmAccount) throw new Error('Wallet not ready')
			if (!status.data) throw new Error('No init data')

			// Sign the EIP-712 typed data with the embedded-wallet EOA
			// (viem LocalAccount from wallet-core) — same key the smart
			// wallet delegates to, so HL sees the expected signer address.
			const sig = (await evmAccount.signTypedData({
				// biome-ignore lint/suspicious/noExplicitAny: typed-data shape comes from server
				domain: status.data.typedData.domain as any,
				// biome-ignore lint/suspicious/noExplicitAny: same
				types: status.data.typedData.types as any,
				// biome-ignore lint/suspicious/noExplicitAny: same
				primaryType: status.data.typedData.primaryType as any,
				// biome-ignore lint/suspicious/noExplicitAny: same
				message: status.data.typedData.message as any,
			})) as Hex

			const res = await authFetch('/api/hyperliquid/agent/confirm', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					walletId: status.data.walletId,
					agentAddress: status.data.agentAddress,
					nonce: status.data.nonce,
					signature: sig,
				}),
			})
			if (!res.ok) {
				const err = (await res.json().catch(() => ({}))) as { error?: string }
				throw new Error(err.error || `confirm ${res.status}`)
			}
			return res.json()
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ['hl-agent'] })
		},
		onError: () => {
			// Force a fresh /init on next click — the cached `status.data`
			// holds the nonce just submitted, so a retry without
			// invalidation would re-send the same nonce and trigger
			// "duplicate nonce" again (HL rejects re-used nonces per user).
			qc.invalidateQueries({ queryKey: ['hl-agent'] })
		},
	})

	return { status, approve }
}
